import { graphql } from 'relay-runtime';

import { keys as sessionStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { type UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

// eslint-disable-next-line import/no-cycle
import { PopularFeedTimeGranularityValue } from './Feed.tsx';
import { useFeedDropdownStorage } from './useFeedDropdownStorage.ts';
import { PopularFeedWithTimeGranularityStorageSnippetsQuery } from './PopularFeedWithTimeGranularityStorageSnippets.graphql.ts';
import { PopularFeedWithTimeGranularityStorageSnippetsV2Query } from './PopularFeedWithTimeGranularityStorageSnippetsV2.graphql.ts';
import type {
	PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
	PopularFeedWithTimeGranularityStorageSnippetsQuery$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsQuery.graphql.ts';
import type {
	PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
	PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsV2Query.graphql.ts';
import type {
	usePopularFeedWithTimeGranularityStorageQuery$data,
	usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';

export const usePopularFeedWithTimeGranularityStorageQuery = graphql`
	query usePopularFeedWithTimeGranularityStorageQuery(
		$first: Int
		$after: String
		$timeGranularity: String = "default"
	) {
		popularFeed(first: $first, after: $after, timeGranularity: $timeGranularity) {
			nodes {
				content {
					title
					id
					type
					subType
					emoji: properties(key: "emoji-title-published") {
						nodes {
							id
							value
						}
					}
					contentProperties {
						latest {
							generatedBy
						}
					}
					excerpt(length: 350)
					labels {
						nodes {
							name
						}
					}
					links {
						webui
					}
					space {
						alias
						id
						key
						name
					}
					history {
						createdDate
						createdBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						ownedBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
						}
					}
					metadata {
						lastModifiedDate
						comments {
							commentsCount
						}
						currentuser {
							favourited {
								isFavourite
							}
						}
					}
					version {
						friendlyWhen
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const usePopularFeedWithTimeGranularityStorage = (
	renderPopularFeed: boolean,
): {
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
	setPopularFeedTimeGranularity: (timeGranularity: PopularFeedTimeGranularityValue) => void;
	popularFeedQuery: UseQueryInteropSupportedResult<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables
	>;
} => {
	const { isRovoLLMEnabled } = useSessionData();

	const { value, setValue, query } = useFeedDropdownStorage<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			// Skip vanilla V1 when ANOTHER query is going to fire for this user.
			// Otherwise vanilla V1 is the safe fallback that must always run.
			//
			// The V2 experiment in Statsig (`confluence_ai_snippets_popular_feed`)
			// uses an `isEnabled` boolean param. All branch checks use
			// `('isEnabled', true)` to detect the V2-treatment arm.
			//
			// V1-with-snippets fires when: in_following_popular_feeds isEnabled=true
			//                              AND NOT V2-treatment
			// V2 fires when:               popular_feed isEnabled=true AND isRovoLLMEnabled
			//
			// Note: we re-call the gate functions inline (rather than caching
			// into a local) so the `confluence-feature-gating/inline-usage`
			// lint rule (which requires gate calls at every callsite for
			// static analysis) is satisfied. The gate runtime memoises results
			// so the duplicate calls are essentially free.
			//
			// Pre-conditions (`renderPopularFeed`, `isRovoLLMEnabled`) are kept
			// at the START of each sub-expression so the gate call is the LAST
			// term, satisfying `confluence-feature-gating/no-preconditioning`.
			skipQuery:
				!renderPopularFeed ||
				// V1-with-snippets serves the user when its gate is on AND they
				// are NOT in the V2-treatment arm. We compute that as:
				//   in_following_popular_feeds=true
				//   AND popular_feed isEnabled != true
				// To keep each gate call at the END of its sub-expression, we
				// short-circuit on the V2-treatment check first via a "not".
				(!UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true) &&
					UNSAFE_expValEqualsNoExposure(
						'confluence_ai_snippets_in_following_popular_feeds',
						'isEnabled',
						true,
					)) ||
				// V2 serves the user when they are Rovo-enabled AND in the
				// V2-treatment arm. Rovo pre-condition goes first; gate last.
				(isRovoLLMEnabled &&
					UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true)),
		},
		{
			query: usePopularFeedWithTimeGranularityStorageQuery,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): usePopularFeedWithTimeGranularityStorageQuery$variables => ({
				timeGranularity,
			}),
		},
	);
	const {
		value: valueWithSnippets,
		setValue: setValueWithSnippets,
		query: queryWithSnippets,
	} = useFeedDropdownStorage<
		PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
		PopularFeedWithTimeGranularityStorageSnippetsQuery$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			skipQuery:
				!renderPopularFeed ||
				!UNSAFE_expValEqualsNoExposure(
					'confluence_ai_snippets_in_following_popular_feeds',
					'isEnabled',
					true,
				) ||
				// Skip V1-with-snippets when the V2 experiment treatment arm is on,
				// regardless of Rovo. Two cases:
				//   1. Rovo-enabled V2-treatment users → served by V2 (no V1).
				//   2. Non-Rovo V2-treatment users → vanilla V1 takes over (it has
				//      no snippet field selections, so a tighter query).
				// Either way, V1-with-snippets is redundant for V2-treatment users.
				UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true),
			interopApolloAggClient: true,
		},
		{
			query: PopularFeedWithTimeGranularityStorageSnippetsQuery,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): PopularFeedWithTimeGranularityStorageSnippetsQuery$variables => ({
				timeGranularity,
				isRovoLLMEnabled: expVal(
					'confluence_ai_snippets_popular_feed_for_ai_enabled',
					'isEnabled',
					false,
				)
					? isRovoLLMEnabled
					: true,
			}),
		},
	);

	// V2 path: uses aiProperty directly on PopularFeedItem (parallel with Confluence hop).
	// Active when confluence_ai_snippets_popular_feed experiment gate is ON
	// AND the user has Rovo LLM enabled.
	// Blocked by: S4 (graphql-central-schema) + S5 (cc-graphql) must be deployed first.
	//
	// EXPOSURE TIMING (important):
	// We MUST gate this experiment's exposure by Rovo LLM enablement BEFORE
	// firing exposure. AI snippets only render for Rovo-enabled users — without
	// Rovo, no snippet is shown and the treatment effect cannot manifest.
	// Exposing non-Rovo users would dilute both arms and produce SRM /
	// exposure imbalance (the same failure mode that hit the
	// `confluence_ai_snippets_in_following_popular_feeds` experiment).
	//
	// We also use `expValEquals` (not `UNSAFE_expValEqualsNoExposure`) inside
	// `skipQuery` so the exposure fires at query-fire time — the actual point
	// of decision. Pairing this with a `renderPopularFeed && isRovoLLMEnabled`
	// pre-gate ensures only the intended user subset (Rovo-enabled users) is
	// ever exposed.
	//
	// What runs when Rovo is NOT enabled (regardless of V2-treatment state):
	//   - V1 vanilla query (no AI snippets at all), or
	//   - V1-with-snippets (if its gate is on; but @include strips aiProperty)
	// Either way, non-Rovo users never see a snippet and are never exposed to
	// the V2 experiment.
	const {
		value: valueWithSnippetsV2,
		setValue: setValueWithSnippetsV2,
		query: queryWithSnippetsV2,
	} = useFeedDropdownStorage<
		PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
		PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			skipQuery:
				!renderPopularFeed ||
				!isRovoLLMEnabled ||
				!expValEquals('confluence_ai_snippets_popular_feed', 'isEnabled', true),
			// Use the AGG (interop) client like V1-with-snippets — V2 also goes through AGG.
			interopApolloAggClient: true,
		},
		{
			query: PopularFeedWithTimeGranularityStorageSnippetsV2Query,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables => ({
				timeGranularity,
				// The V2 `skipQuery` above already gates on `!isRovoLLMEnabled`,
				// so this variables function is only ever invoked when Rovo LLM
				// is enabled. Pass `true` unconditionally — avoids dead-code
				// branching and prevents a spurious exposure of
				// `confluence_ai_snippets_popular_feed_for_ai_enabled` from
				// firing on every V2 query. This mirrors how `preloadFeed.ts`
				// already handles this (also passes `true` unconditionally in
				// the V2 branch).
				isRovoLLMEnabled: true,
			}),
		},
	);

	// V2 (parallel hydration) is only taken when the user is explicitly assigned
	// to the V2-treatment arm. When the experiment is not yet created in
	// Statsig, the user is not enrolled, or the experiment client cannot reach
	// Statsig, expValEquals returns false → V1 (legacy sequential) is the safe
	// fallback. Note: experiment calls are inlined at every callsite to satisfy
	// the `confluence-feature-gating/inline-usage` rule, which requires gates
	// to be statically analyzable.

	// Pre-select V1 vs V1-with-snippets to avoid nested ternaries below.
	// Picks V1-with-snippets only when its gate is on AND we're not in the V2
	// arm (where V1-with-snippets is suppressed in favour of vanilla V1).
	// This mirrors the V1-with-snippets `skipQuery` logic above and ensures
	// non-Rovo V2-treatment users (who fall back to vanilla V1) read from the
	// query that actually fires for them.
	const useV1WithSnippets =
		UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		) && !UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true);
	const v1Granularity = useV1WithSnippets ? valueWithSnippets : value;
	const v1Setter = useV1WithSnippets ? setValueWithSnippets : setValue;
	const v1Query = useV1WithSnippets ? queryWithSnippets : query;

	// Exposure for `confluence_ai_snippets_popular_feed` is fired exactly once,
	// at V2 query-fire time inside the V2 `skipQuery` above (via `expValEquals`).
	// All subsequent checks here use `UNSAFE_expValEqualsNoExposure` so we don't
	// log duplicate exposures on every render. This matches the "single point of
	// decision" requirement to avoid SRM / exposure imbalance.
	const isV2Active =
		isRovoLLMEnabled &&
		UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true);
	return {
		popularFeedTimeGranularity: isV2Active ? valueWithSnippetsV2 : v1Granularity,
		setPopularFeedTimeGranularity: isV2Active ? setValueWithSnippetsV2 : v1Setter,
		// Cast required: V2 query has a structurally compatible shape (the
		// `aiProperty` field is additive on PopularFeedItem) but TS can't infer
		// the union assignability for legacy callers that expect the V1 type.
		// Safe at runtime because PopularFeedCard reads only `content`/`id`.
		popularFeedQuery: (isV2Active
			? queryWithSnippetsV2
			: v1Query) as UseQueryInteropSupportedResult<
			usePopularFeedWithTimeGranularityStorageQuery$data,
			usePopularFeedWithTimeGranularityStorageQuery$variables
		>,
	};
};
